import { useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Trash2, Edit, Eye, EyeOff, Plus, Upload, X, Loader2, ExternalLink,
  Sparkles, ChevronDown, ChevronUp,
} from "lucide-react";
import type {
  BlogPost, BlogFaq, BlogComparative, BlogSource, BlogTypology, BlogDifferential,
} from "@/lib/blog";
import { fetchAllPosts } from "@/lib/blog";
import { fetchListings, type Listing } from "@/lib/listings";
import { fetchLaunches, type Launch } from "@/lib/launches";
import { BLOG_SUGGESTIONS, type BlogSuggestion } from "@/lib/blogSuggestions";

const BUCKET = "listing-images";

type FormState = {
  title: string; subtitle: string; cover_image: string; content: string; excerpt: string;
  author: string; tags: string; meta_description: string; keywords: string;
  is_published: boolean; similar_listing_ids: string[];
  // SEO avançado
  seo_title: string; canonical_url: string; og_image: string;
  twitter_card: string; schema_type: string; geo_region: string;
  // Repeaters
  faqs: BlogFaq[]; comparatives: BlogComparative[]; sources: BlogSource[];
  typologies: BlogTypology[]; differentials: BlogDifferential[];
  // Objetos livres
  general_info: Record<string, string>;
  location_info: Record<string, string>;
  audience: Record<string, string>;
  market_data: Record<string, string>;
  airbnb_data: Record<string, string>;
  condo_info: Record<string, string>;
  ai_content: Record<string, string>;
  cta: Record<string, string>;
  geo: Record<string, string>;
  related_neighborhoods: string[];
  related_launch_ids: string[];
};

const emptyForm: FormState = {
  title: "", subtitle: "", cover_image: "", content: "", excerpt: "",
  author: "TopClass Imóveis", tags: "", meta_description: "", keywords: "",
  is_published: false, similar_listing_ids: [],
  seo_title: "", canonical_url: "", og_image: "",
  twitter_card: "summary_large_image", schema_type: "Article", geo_region: "",
  faqs: [], comparatives: [], sources: [], typologies: [], differentials: [],
  general_info: {}, location_info: {}, audience: {}, market_data: {}, airbnb_data: {},
  condo_info: {}, ai_content: {}, cta: {}, geo: {},
  related_neighborhoods: [], related_launch_ids: [],
};

const NEIGHBORHOODS = [
  "Copacabana","Ipanema","Leblon","Lagoa","Botafogo","Barra da Tijuca",
  "Recreio dos Bandeirantes","Tijuca","Jardim Botânico","Gávea","Flamengo","Urca",
];

// ---------- Sub-components ----------

const Section = ({ title, children, defaultOpen = false }: { title: string; children: ReactNode; defaultOpen?: boolean }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && <div className="p-3 pt-0 space-y-3">{children}</div>}
    </div>
  );
};

const Counter = ({ value, max }: { value: string; max: number }) => (
  <span className={`text-xs ${value.length > max ? "text-destructive" : "text-muted-foreground"}`}>
    {value.length}/{max}
  </span>
);

function ObjectFields<T extends Record<string, string>>({
  value, onChange, fields,
}: { value: T; onChange: (v: T) => void; fields: { key: keyof T & string; label: string; textarea?: boolean; placeholder?: string }[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {fields.map((f) => (
        <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
          <label className="text-xs font-medium">{f.label}</label>
          {f.textarea ? (
            <Textarea rows={2} value={value[f.key] || ""} placeholder={f.placeholder}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value } as T)} />
          ) : (
            <Input value={value[f.key] || ""} placeholder={f.placeholder}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value } as T)} />
          )}
        </div>
      ))}
    </div>
  );
}

function Repeater<T>({
  items, onChange, blank, render, addLabel,
}: { items: T[]; onChange: (v: T[]) => void; blank: T; addLabel: string; render: (item: T, update: (v: T) => void) => ReactNode }) {
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="border border-border rounded p-2 space-y-2 relative">
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="absolute top-1 right-1 text-destructive hover:bg-destructive/10 rounded p-1">
            <X className="h-3 w-3" />
          </button>
          {render(it, (v) => onChange(items.map((x, j) => (j === i ? v : x))))}
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, structuredClone(blank)])}
        className="text-xs text-primary hover:underline inline-flex items-center gap-1">
        <Plus className="h-3 w-3" /> {addLabel}
      </button>
    </div>
  );
}

// ---------- Previews ----------

const SerpPreview = ({ title, url, description }: { title: string; url: string; description: string }) => (
  <div className="border border-border rounded p-3 bg-background">
    <div className="text-xs text-green-700 truncate">{url}</div>
    <div className="text-blue-700 text-lg leading-tight truncate">{title || "Título do post"}</div>
    <div className="text-sm text-muted-foreground line-clamp-2">{description || "Descrição do post..."}</div>
  </div>
);

const SocialPreview = ({ title, description, image }: { title: string; description: string; image: string }) => (
  <div className="border border-border rounded overflow-hidden bg-background max-w-md">
    {image ? (
      <img src={image} alt="" className="w-full aspect-[1.91/1] object-cover" />
    ) : (
      <div className="w-full aspect-[1.91/1] bg-muted flex items-center justify-center text-xs text-muted-foreground">Sem imagem</div>
    )}
    <div className="p-3">
      <div className="text-xs uppercase text-muted-foreground">topclassimoveis.com</div>
      <div className="text-sm font-semibold line-clamp-2">{title || "Título social"}</div>
      <div className="text-xs text-muted-foreground line-clamp-2">{description}</div>
    </div>
  </div>
);

// ---------- Main ----------

const BlogAdmin = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [launches, setLaunches] = useState<Launch[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalPost, setOriginalPost] = useState<BlogPost | null>(null);
  const [form, setForm] = useState<FormState>({ ...emptyForm });
  const [uploading, setUploading] = useState(false);
  const [uploadingOg, setUploadingOg] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);


  const slug = form.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const previewUrl = `topclassimoveis.com/blog/${slug || "post"}`;
  const previewTitle = form.seo_title || form.title;
  const previewDesc = form.meta_description || form.excerpt;
  const previewImg = form.og_image || form.cover_image;

  const applySuggestion = (s: BlogSuggestion) => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      title: s.title, subtitle: s.subtitle, content: s.content, excerpt: s.excerpt,
      tags: s.tags.join(", "), meta_description: s.meta_description, keywords: s.keywords,
    });
    setShowSuggestions(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    load();
    fetchListings().then(setListings).catch(() => {});
    fetchLaunches().then(setLaunches).catch(() => {});
  }, []);

  const load = async () => {
    try { setPosts(await fetchAllPosts()); }
    catch (e: any) { toast({ title: "Erro ao carregar posts", description: e.message, variant: "destructive" }); }
  };

  const reset = () => { setForm({ ...emptyForm }); setEditingId(null); setOriginalPost(null); };


  const uploadImage = async (file: File, target: "cover" | "og") => {
    if (!file.type.startsWith("image/")) return;
    const setLoad = target === "og" ? setUploadingOg : setUploading;
    setLoad(true);
    const ext = file.name.split(".").pop();
    const path = `blog/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { cacheControl: "3600", upsert: false });
    if (error) toast({ title: "Erro no upload", description: error.message, variant: "destructive" });
    else {
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
      setForm((f) => target === "og" ? { ...f, og_image: data.publicUrl } : { ...f, cover_image: data.publicUrl });
    }
    setLoad(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const publishedAt = (() => {
      if (!form.is_published) return originalPost?.published_at || null;
      if (!editingId) return new Date().toISOString();
      if (!originalPost?.is_published) return new Date().toISOString();
      return originalPost.published_at || new Date().toISOString();
    })();
    const payload = {
      title: form.title,
      subtitle: form.subtitle || null,
      cover_image: form.cover_image || null,
      content: form.content,
      excerpt: form.excerpt || null,
      author: form.author || "TopClass Imóveis",
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      meta_description: form.meta_description || null,
      keywords: form.keywords || null,
      is_published: form.is_published,
      published_at: publishedAt,
      similar_listing_ids: form.similar_listing_ids,
      seo_title: form.seo_title || null,
      canonical_url: form.canonical_url || null,
      og_image: form.og_image || null,
      twitter_card: form.twitter_card || "summary_large_image",
      schema_type: form.schema_type || "Article",
      geo_region: form.geo_region || null,
      faqs: form.faqs,
      comparatives: form.comparatives,
      sources: form.sources,
      typologies: form.typologies,
      differentials: form.differentials,
      general_info: form.general_info,
      location_info: form.location_info,
      audience: form.audience,
      market_data: form.market_data,
      airbnb_data: form.airbnb_data,
      condo_info: form.condo_info,
      ai_content: form.ai_content,
      cta: form.cta,
      geo: form.geo,
      related_neighborhoods: form.related_neighborhoods,
      related_launch_ids: form.related_launch_ids,
    };

    const q = editingId
      ? supabase.from("blog_posts" as any).update(payload).eq("id", editingId)
      : supabase.from("blog_posts" as any).insert(payload);
    const { error } = await q;
    if (error) { toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" }); return; }
    toast({ title: editingId ? "Post atualizado" : "Post criado" });
    reset();
    load();
  };

  const edit = (p: BlogPost) => {
    setEditingId(p.id);
    setOriginalPost(p);
    setForm({
      ...emptyForm,
      title: p.title, subtitle: p.subtitle || "", cover_image: p.cover_image || "",
      content: p.content, excerpt: p.excerpt || "", author: p.author || "TopClass Imóveis",
      tags: (p.tags || []).join(", "), meta_description: p.meta_description || "",
      keywords: p.keywords || "", is_published: p.is_published,
      similar_listing_ids: p.similar_listing_ids || [],
      seo_title: p.seo_title || "", canonical_url: p.canonical_url || "",
      og_image: p.og_image || "", twitter_card: p.twitter_card || "summary_large_image",
      schema_type: p.schema_type || "Article", geo_region: p.geo_region || "",
      faqs: p.faqs || [], comparatives: p.comparatives || [], sources: p.sources || [],
      typologies: p.typologies || [], differentials: p.differentials || [],
      general_info: (p.general_info as any) || {}, location_info: (p.location_info as any) || {},
      audience: (p.audience as any) || {}, market_data: (p.market_data as any) || {},
      airbnb_data: (p.airbnb_data as any) || {}, condo_info: (p.condo_info as any) || {},
      ai_content: (p.ai_content as any) || {}, cta: (p.cta as any) || {}, geo: (p.geo as any) || {},
      related_neighborhoods: p.related_neighborhoods || [],
      related_launch_ids: p.related_launch_ids || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };


  const togglePublish = async (p: BlogPost) => {
    const { error } = await supabase.from("blog_posts" as any)
      .update({ is_published: !p.is_published, published_at: !p.is_published ? new Date().toISOString() : p.published_at })
      .eq("id", p.id);
    if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
    else load();
  };

  const remove = async (p: BlogPost) => {
    if (!confirm(`Excluir "${p.title}"?`)) return;
    const { error } = await supabase.from("blog_posts" as any).delete().eq("id", p.id);
    if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
    else { toast({ title: "Post excluído" }); load(); }
  };

  const grouped = BLOG_SUGGESTIONS.reduce<Record<string, BlogSuggestion[]>>((acc, s) => {
    (acc[s.landingPage] ||= []).push(s); return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Sugestões */}
      <div className="bg-card rounded-lg border border-border">
        <button type="button" onClick={() => setShowSuggestions((v) => !v)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-display font-bold">Sugestões de posts para landing pages ({BLOG_SUGGESTIONS.length})</span>
          </div>
          {showSuggestions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showSuggestions && (
          <div className="p-4 pt-0 space-y-4">
            {Object.entries(grouped).map(([landing, items]) => (
              <div key={landing} className="border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold">→ {landing}</h4>
                  <a href={items[0].landingUrl} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                    Ver landing <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {items.map((s, i) => (
                    <button key={i} type="button" onClick={() => applySuggestion(s)}
                      className="text-left p-3 rounded border border-border hover:border-primary hover:bg-muted/30">
                      <div className="text-sm font-medium line-clamp-2">{s.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{s.excerpt}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={handleSubmit} className="lg:col-span-2 bg-card rounded-lg p-5 shadow-lg border border-border space-y-4">
          <h2 className="font-display text-lg font-bold">
            {editingId ? "Editar post" : "Novo post"} — Centro de Conteúdo Inteligente
          </h2>

          {/* Informações Gerais */}
          <Section title="1. Informações Gerais" defaultOpen>
            <div>
              <label className="text-sm font-medium">Título*</label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Subtítulo</label>
              <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Foto de capa</label>
              <div className="flex items-center gap-2 mt-1">
                {form.cover_image && (
                  <div className="relative">
                    <img src={form.cover_image} alt="capa" className="h-16 w-24 object-cover rounded border" />
                    <button type="button" onClick={() => setForm({ ...form, cover_image: "" })}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-1 rounded border border-dashed border-border px-3 py-2 text-sm hover:border-primary">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  <span>{uploading ? "Enviando..." : "Enviar imagem"}</span>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "cover")} />
                </label>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Resumo (excerpt)</label>
              <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Conteúdo (Markdown)*</label>
              <Textarea required rows={10} value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="font-mono text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Autor</label>
              <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Tags (separadas por vírgula)</label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            </div>
          </Section>

          {/* Localização */}
          <Section title="2. Localização">
            <ObjectFields value={form.location_info} onChange={(v) => setForm({ ...form, location_info: v })}
              fields={[
                { key: "neighborhood", label: "Bairro" },
                { key: "city", label: "Cidade" },
                { key: "state", label: "Estado" },
                { key: "region", label: "Região" },
                { key: "landmarks", label: "Pontos de referência", textarea: true },
                { key: "transport", label: "Transporte / Acesso", textarea: true },
              ]} />
          </Section>

          {/* Público */}
          <Section title="3. Público-alvo">
            <ObjectFields value={form.audience} onChange={(v) => setForm({ ...form, audience: v })}
              fields={[
                { key: "primary", label: "Público principal" },
                { key: "income", label: "Faixa de renda" },
                { key: "profile", label: "Perfil (família/investidor/etc)" },
                { key: "goals", label: "Objetivos", textarea: true },
              ]} />
          </Section>

          {/* Dados do Mercado */}
          <Section title="4. Dados do Mercado">
            <ObjectFields value={form.market_data} onChange={(v) => setForm({ ...form, market_data: v })}
              fields={[
                { key: "price_m2", label: "Preço médio m²" },
                { key: "yoy", label: "Valorização últimos 12 meses" },
                { key: "avg_ticket", label: "Ticket médio" },
                { key: "supply", label: "Oferta atual" },
                { key: "trend", label: "Tendência", textarea: true },
              ]} />
          </Section>

          {/* Airbnb */}
          <Section title="5. Dados Airbnb">
            <ObjectFields value={form.airbnb_data} onChange={(v) => setForm({ ...form, airbnb_data: v })}
              fields={[
                { key: "daily_rate", label: "Diária média" },
                { key: "occupancy", label: "Ocupação (%)" },
                { key: "monthly_income", label: "Renda mensal estimada" },
                { key: "roi", label: "ROI estimado" },
                { key: "notes", label: "Observações", textarea: true },
              ]} />
          </Section>

          {/* Tipologias */}
          <Section title="6. Tipologias">
            <Repeater<BlogTypology> items={form.typologies} onChange={(v) => setForm({ ...form, typologies: v })}
              blank={{ name: "", area: "", bedrooms: "", price: "", notes: "" }} addLabel="Adicionar tipologia"
              render={(t, u) => (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Input placeholder="Nome" value={t.name} onChange={(e) => u({ ...t, name: e.target.value })} />
                  <Input placeholder="Área" value={t.area || ""} onChange={(e) => u({ ...t, area: e.target.value })} />
                  <Input placeholder="Quartos" value={t.bedrooms || ""} onChange={(e) => u({ ...t, bedrooms: e.target.value })} />
                  <Input placeholder="Preço" value={t.price || ""} onChange={(e) => u({ ...t, price: e.target.value })} />
                  <Textarea rows={1} className="md:col-span-4" placeholder="Notas" value={t.notes || ""}
                    onChange={(e) => u({ ...t, notes: e.target.value })} />
                </div>
              )} />
          </Section>

          {/* Condomínio */}
          <Section title="7. Condomínio">
            <ObjectFields value={form.condo_info} onChange={(v) => setForm({ ...form, condo_info: v })}
              fields={[
                { key: "name", label: "Nome" },
                { key: "monthly_fee", label: "Taxa mensal" },
                { key: "amenities", label: "Amenidades", textarea: true },
                { key: "rules", label: "Regras / Airbnb permitido", textarea: true },
              ]} />
          </Section>

          {/* Diferenciais */}
          <Section title="8. Diferenciais">
            <Repeater<BlogDifferential> items={form.differentials} onChange={(v) => setForm({ ...form, differentials: v })}
              blank={{ title: "", description: "" }} addLabel="Adicionar diferencial"
              render={(d, u) => (
                <div className="space-y-1">
                  <Input placeholder="Título" value={d.title} onChange={(e) => u({ ...d, title: e.target.value })} />
                  <Textarea rows={2} placeholder="Descrição" value={d.description || ""}
                    onChange={(e) => u({ ...d, description: e.target.value })} />
                </div>
              )} />
          </Section>

          {/* Conteúdo IA */}
          <Section title="9. Conteúdo para IA (resumos semânticos)">
            <ObjectFields value={form.ai_content} onChange={(v) => setForm({ ...form, ai_content: v })}
              fields={[
                { key: "summary", label: "Resumo curto para assistentes de IA", textarea: true },
                { key: "key_points", label: "Pontos-chave (uma linha cada)", textarea: true },
                { key: "entities", label: "Entidades relacionadas (bairros, marcas)", textarea: true },
              ]} />
          </Section>

          {/* FAQ */}
          <Section title="10. FAQ (Perguntas Frequentes)">
            <Repeater<BlogFaq> items={form.faqs} onChange={(v) => setForm({ ...form, faqs: v })}
              blank={{ question: "", answer: "" }} addLabel="Adicionar pergunta"
              render={(q, u) => (
                <div className="space-y-1">
                  <Input placeholder="Pergunta" value={q.question} onChange={(e) => u({ ...q, question: e.target.value })} />
                  <Textarea rows={2} placeholder="Resposta" value={q.answer} onChange={(e) => u({ ...q, answer: e.target.value })} />
                </div>
              )} />
          </Section>

          {/* Schema */}
          <Section title="11. Schema (dados estruturados)">
            <div>
              <label className="text-sm font-medium">Tipo de Schema</label>
              <select className="w-full border border-input rounded h-10 px-3 bg-background"
                value={form.schema_type} onChange={(e) => setForm({ ...form, schema_type: e.target.value })}>
                {["Article","BlogPosting","NewsArticle","RealEstateListing","FAQPage"].map((v) => <option key={v}>{v}</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Gerado automaticamente + FAQPage se houver FAQs + BreadcrumbList.</p>
            </div>
          </Section>

          {/* SEO Avançado */}
          <Section title="12. SEO Avançado" defaultOpen>
            <div>
              <div className="flex items-center justify-between"><label className="text-sm font-medium">Título SEO</label><Counter value={form.seo_title} max={60} /></div>
              <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder="Se vazio, usa o título do post" />
            </div>
            <div>
              <div className="flex items-center justify-between"><label className="text-sm font-medium">Meta description</label><Counter value={form.meta_description} max={160} /></div>
              <Textarea rows={2} maxLength={200} value={form.meta_description}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Keywords</label>
              <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">URL canônica (opcional)</label>
              <Input value={form.canonical_url} onChange={(e) => setForm({ ...form, canonical_url: e.target.value })}
                placeholder="https://topclassimoveis.com/blog/..." />
            </div>
            <div>
              <label className="text-sm font-medium">Imagem Open Graph (1200×630)</label>
              <div className="flex items-center gap-2 mt-1">
                {form.og_image && (
                  <div className="relative">
                    <img src={form.og_image} alt="og" className="h-16 w-28 object-cover rounded border" />
                    <button type="button" onClick={() => setForm({ ...form, og_image: "" })}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-1 rounded border border-dashed border-border px-3 py-2 text-sm hover:border-primary">
                  {uploadingOg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  <span>{uploadingOg ? "Enviando..." : "Enviar OG"}</span>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "og")} />
                </label>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Twitter Card</label>
              <select className="w-full border border-input rounded h-10 px-3 bg-background"
                value={form.twitter_card} onChange={(e) => setForm({ ...form, twitter_card: e.target.value })}>
                <option value="summary_large_image">summary_large_image</option>
                <option value="summary">summary</option>
              </select>
            </div>

            {/* Previews */}
            <div className="space-y-2 pt-2">
              <p className="text-xs font-medium">Prévia da SERP do Google</p>
              <SerpPreview title={previewTitle} url={previewUrl} description={previewDesc} />
              <p className="text-xs font-medium">Prévia de compartilhamento social</p>
              <SocialPreview title={previewTitle} description={previewDesc} image={previewImg} />
            </div>
          </Section>

          {/* Relacionamentos */}
          <Section title="13. Relacionamentos">
            <div>
              <label className="text-sm font-medium block mb-1">Imóveis Semelhantes ({form.similar_listing_ids.length})</label>
              <div className="max-h-40 overflow-y-auto border border-border rounded-md p-2 space-y-1">
                {listings.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-xs p-1 hover:bg-muted rounded cursor-pointer">
                    <input type="checkbox" checked={form.similar_listing_ids.includes(l.id)}
                      onChange={(e) => setForm({
                        ...form, similar_listing_ids: e.target.checked
                          ? [...form.similar_listing_ids, l.id]
                          : form.similar_listing_ids.filter((id) => id !== l.id),
                      })} />
                    <span className="flex-1 truncate">{l.title} — {l.neighborhood}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Bairros relacionados</label>
              <div className="flex flex-wrap gap-1">
                {NEIGHBORHOODS.map((n) => {
                  const on = form.related_neighborhoods.includes(n);
                  return (
                    <button key={n} type="button"
                      onClick={() => setForm({ ...form, related_neighborhoods: on ? form.related_neighborhoods.filter((x) => x !== n) : [...form.related_neighborhoods, n] })}
                      className={`text-xs px-2 py-1 rounded border ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Lançamentos relacionados ({form.related_launch_ids.length})</label>
              <div className="max-h-32 overflow-y-auto border border-border rounded-md p-2 space-y-1">
                {launches.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-xs p-1 hover:bg-muted rounded cursor-pointer">
                    <input type="checkbox" checked={form.related_launch_ids.includes(l.id)}
                      onChange={(e) => setForm({
                        ...form, related_launch_ids: e.target.checked
                          ? [...form.related_launch_ids, l.id]
                          : form.related_launch_ids.filter((id) => id !== l.id),
                      })} />
                    <span className="flex-1 truncate">{l.name} — {l.neighborhood}</span>
                  </label>
                ))}
                {launches.length === 0 && <p className="text-xs text-muted-foreground">Nenhum lançamento.</p>}
              </div>
            </div>
          </Section>

          {/* Comparativos */}
          <Section title="14. Comparativos">
            <Repeater<BlogComparative> items={form.comparatives} onChange={(v) => setForm({ ...form, comparatives: v })}
              blank={{ label: "", value: "", note: "" }} addLabel="Adicionar comparativo"
              render={(c, u) => (
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Rótulo (ex: Ipanema)" value={c.label} onChange={(e) => u({ ...c, label: e.target.value })} />
                  <Input placeholder="Valor (ex: R$ 22 mil/m²)" value={c.value} onChange={(e) => u({ ...c, value: e.target.value })} />
                  <Input placeholder="Nota" value={c.note || ""} onChange={(e) => u({ ...c, note: e.target.value })} />
                </div>
              )} />
            <p className="text-xs font-medium mt-3">Fontes citadas</p>
            <Repeater<BlogSource> items={form.sources} onChange={(v) => setForm({ ...form, sources: v })}
              blank={{ label: "", url: "" }} addLabel="Adicionar fonte"
              render={(s, u) => (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Nome" value={s.label} onChange={(e) => u({ ...s, label: e.target.value })} />
                  <Input placeholder="URL" value={s.url} onChange={(e) => u({ ...s, url: e.target.value })} />
                </div>
              )} />
          </Section>

          {/* CTA */}
          <Section title="15. CTA (Call to Action)">
            <ObjectFields value={form.cta} onChange={(v) => setForm({ ...form, cta: v })}
              fields={[
                { key: "primary_text", label: "Texto do botão principal", placeholder: "Fale com um especialista" },
                { key: "primary_url", label: "URL / WhatsApp" },
                { key: "secondary_text", label: "Texto secundário" },
                { key: "secondary_url", label: "URL secundária" },
              ]} />
          </Section>

          {/* GEO */}
          <Section title="16. GEO (Coordenadas)">
            <ObjectFields value={form.geo} onChange={(v) => setForm({ ...form, geo: v })}
              fields={[
                { key: "latitude", label: "Latitude" },
                { key: "longitude", label: "Longitude" },
                { key: "place_name", label: "Nome do local" },
              ]} />
            <div>
              <label className="text-sm font-medium">Região geográfica (ex: BR-RJ)</label>
              <Input value={form.geo_region} onChange={(e) => setForm({ ...form, geo_region: e.target.value })} />
            </div>
          </Section>

          {/* Metadados */}
          <Section title="17. Metadados (dados gerais)">
            <ObjectFields value={form.general_info} onChange={(v) => setForm({ ...form, general_info: v })}
              fields={[
                { key: "category", label: "Categoria" },
                { key: "reading_time", label: "Tempo de leitura (min)" },
                { key: "language", label: "Idioma", placeholder: "pt-BR" },
                { key: "notes", label: "Observações internas", textarea: true },
              ]} />
          </Section>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
            Publicar imediatamente
          </label>

          <div className="flex gap-2 pt-2">
            <Button type="submit" className="flex-1">
              <Plus className="h-4 w-4" /> {editingId ? "Salvar" : "Criar post"}
            </Button>
            {editingId && <Button type="button" variant="outline" onClick={reset}>Cancelar</Button>}
          </div>
        </form>

        <div className="space-y-3">
          <h2 className="font-display text-lg font-bold">Posts ({posts.length})</h2>
          {posts.length === 0 && <p className="text-muted-foreground text-sm">Nenhum post ainda.</p>}
          {posts.map((p) => (
            <div key={p.id} className="bg-card rounded-lg p-3 border border-border flex gap-3 items-start">
              {p.cover_image && <img src={p.cover_image} alt={p.title} className="h-16 w-24 object-cover rounded" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${p.is_published ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}>
                    {p.is_published ? "Publicado" : "Rascunho"}
                  </span>
                </div>
                <h3 className="font-display font-bold text-sm truncate">{p.title}</h3>
                <div className="text-xs text-muted-foreground truncate">/blog/{p.slug}</div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="outline" onClick={() => edit(p)}><Edit className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => togglePublish(p)}>
                  {p.is_published ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </Button>
                {p.is_published && (
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-8 w-8 rounded border border-border">
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                <Button size="sm" variant="destructive" onClick={() => remove(p)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogAdmin;
